import { useCallback, useState } from "react";

import type { Journey } from "../contract/types";

/**
 * Portée des outils du panneau « Véhicule & Outils ».
 *
 *  - **réseau** : le moteur balaie tout le réseau et propose dynamiquement ses
 *    mesures (aide à la décision, sorties à découvert, fins de service…) ;
 *  - **véhicule** : les mêmes outils, restreints à UN véhicule — ce qui le
 *    concerne, et rien d'autre.
 *
 * Avant ce découpage, les outils affichaient le réseau entier même quand un
 * véhicule était sélectionné : les deux modes se mélangeaient.
 */
export type ToolsScope = { kind: "network" } | { kind: "vehicle"; journey: Journey };

/** Référence stable : évite de re-rendre les outils quand rien ne change. */
export const NETWORK_SCOPE: ToolsScope = { kind: "network" };

export type ToolsScopeMode = ToolsScope["kind"];

/**
 * Portée courante des outils, dérivée de la sélection :
 *  - aucun véhicule sélectionné → **réseau** ;
 *  - un véhicule est sélectionné → **véhicule** d'emblée (cliquer un véhicule
 *    cible les outils sur lui) ;
 *  - l'utilisateur peut repasser en **réseau** tout en gardant le véhicule
 *    ouvert (`setMode("network")`). Ce choix ne vaut que TANT QUE ce véhicule
 *    reste sélectionné : changer de véhicule, ou fermer, l'oublie — y revenir plus
 *    tard rouvre bien en mode véhicule.
 */
export function useToolsScope(journey: Journey | null): {
  scope: ToolsScope;
  mode: ToolsScopeMode;
  setMode: (mode: ToolsScopeMode) => void;
} {
  const [networkForId, setNetworkForId] = useState<string | null>(null);

  // La mise de côté ne survit pas à un changement de sélection (ajustement d'état
  // pendant le rendu, motif prévu par React : pas d'effet, pas de rendu intermédiaire).
  const currentId = journey?.id ?? null;
  if (networkForId !== null && networkForId !== currentId) setNetworkForId(null);

  const focused = journey !== null && networkForId !== journey.id;
  // `scope` change de référence à chaque rafraîchissement live de la course :
  // les outils ne doivent dépendre que de `journey.id` / `journey.runId`, jamais
  // de l'objet.
  const scope: ToolsScope = focused ? { kind: "vehicle", journey } : NETWORK_SCOPE;

  const setMode = useCallback(
    (mode: ToolsScopeMode) => setNetworkForId(mode === "network" && journey ? journey.id : null),
    [journey],
  );

  return { scope, mode: scope.kind, setMode };
}
